/**
 * Settings — overlay scene for volume, language, name, mute.
 *
 * Launched as an overlay on top of the calling scene; closes back to it
 * via `data.from`.
 */
import { Theme } from '../ui/Theme.js';
import { Button } from '../ui/Button.js';
import { platformStorage } from '../save/Storage.js';

export class Settings extends Phaser.Scene {
  constructor() {
    super('Settings');
  }

  init(data) {
    this.from = (data && data.from) || 'MainMenu';
  }

  create() {
    const reg = this.registry;
    const audio = reg.get('audio');
    const i18n = reg.get('i18n');
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Simple "rebuild on resize" for this overlay scene. Settings is
    // stateless (values live in Storage) so restarting is cheap and
    // produces a correct layout at the new dimensions.
    //
    // EXCEPTION: if a DOM <input> is currently focused (i.e. the user
    // is typing their name), skip the restart. On mobile the virtual
    // keyboard appearing/disappearing fires a viewport resize, and a
    // scene restart destroys the DOM input, which immediately closes
    // the keyboard — producing the "keyboard flashes and disappears"
    // bug. Orientation changes while actively typing are rare; if it
    // happens the user can close and re-open Settings to relayout.
    const resizeHandler = () => {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      try { this.scene.restart({ from: this.from }); } catch (_) { /* ignore */ }
    };
    this.scale.on('resize', resizeHandler);
    this.events.once('shutdown', () => this.scale.off('resize', resizeHandler));

    // Dim backdrop.
    this.add.rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.6).setInteractive();

    const panelW = Math.min(380, this.scale.width - 40);
    const panelH = 460;
    const panel = this.add.graphics();
    panel.fillStyle(Theme.colors.surface, 1);
    panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, Theme.radius);

    this.add.text(cx, cy - panelH / 2 + 30, 'Settings', {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.lg,
      color: Theme.colors.text,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Player name — inline DOM <input>, same styling as the lobby join-code
    // input (`PromptModal`) but embedded directly in the Settings panel
    // instead of opened as a popup. Persists on every keystroke so even an
    // accidental scene restart (Language / Mute / Music vol) doesn't lose
    // typed characters. Empty values are ignored so clearing the field
    // can't wipe the stored name.
    const playerName = platformStorage.get('playerName', 'Player');
    this.add.text(cx - panelW / 2 + 30, cy - 145, 'Name', {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.sm,
      color: Theme.colors.textDim
    }).setOrigin(0, 0.5);

    const inputW = panelW - 60;
    const nameInputStyle = [
      'width: ' + inputW + 'px',
      'padding: 10px 14px',
      'font-size: 18px',
      'font-family: ' + (Theme.font && Theme.font.family ? Theme.font.family : 'inherit'),
      'text-align: left',
      'color: #fff',
      'background: rgba(0, 0, 0, 0.45)',
      'border: 2px solid rgba(255, 255, 255, 0.22)',
      'border-radius: 10px',
      'outline: none',
      'box-sizing: border-box',
      '-webkit-tap-highlight-color: transparent'
    ].join('; ');

    const nameDom = this.add.dom(cx, cy - 115, 'input', nameInputStyle, playerName);
    const nameInput = nameDom.node;
    nameInput.type = 'text';
    nameInput.maxLength = 20;
    nameInput.placeholder = 'Player';
    nameInput.autocomplete = 'off';
    nameInput.autocapitalize = 'words';
    nameInput.autocorrect = 'off';
    nameInput.spellcheck = false;

    const persistName = () => {
      const v = (nameInput.value || '').trim().slice(0, 20);
      if (v) platformStorage.set('playerName', v);
    };
    nameInput.addEventListener('input', persistName);
    nameInput.addEventListener('change', persistName);
    nameInput.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        persistName();
        nameInput.blur();
      }
    });

    // Language picker (cycles through registered locales).
    const langs = i18n.getAvailableLanguages();
    new Button({
      scene: this, x: cx, y: cy - 40,
      label: 'Language: ' + i18n.lang,
      variant: 'secondary',
      onClick: () => {
        if (!langs.length) return;
        const idx = Math.max(0, langs.indexOf(i18n.lang));
        const next = langs[(idx + 1) % langs.length];
        i18n.setLanguage(next);
        this.scene.restart({ from: this.from });
      }
    });

    // Mute toggle.
    new Button({
      scene: this, x: cx, y: cy + 20,
      label: audio.muted ? 'Sound: OFF' : 'Sound: ON',
      variant: 'secondary',
      onClick: () => {
        audio.setMuted(!audio.muted);
        this.scene.restart({ from: this.from });
      }
    });

    // Music volume +/-.
    new Button({
      scene: this, x: cx - 60, y: cy + 80,
      label: 'Music –',
      width: 100,
      variant: 'secondary',
      onClick: () => { audio.setMusicVolume(audio.musicVolume - 0.1); this.scene.restart({ from: this.from }); }
    });
    new Button({
      scene: this, x: cx + 60, y: cy + 80,
      label: 'Music +',
      width: 100,
      variant: 'secondary',
      onClick: () => { audio.setMusicVolume(audio.musicVolume + 0.1); this.scene.restart({ from: this.from }); }
    });

    new Button({
      scene: this, x: cx, y: cy + panelH / 2 - 40,
      label: 'Close',
      onClick: () => this.scene.stop()
    });
  }
}
